import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { DocManiaLogo, DocManiaLogoImage } from '../components/DocManiaLogo';
import { CreditBadge } from '../components/CreditBadge';
import { toast } from 'sonner';
import { 
  FileText, CreditCard, History, LogOut, Settings, 
  Coins, ArrowLeft, Check, Loader2, User, Sparkles,
  Camera, FileArchive, Briefcase, Award, Handshake, Landmark, ShoppingCart
} from 'lucide-react';
import { resolveCreditCost } from '../lib/dashboardNavConfig';

/** Labels + API keys for the credit usage guide (costs come from /service-credits). */
const CREDIT_USAGE_ITEMS = [
  { key: 'document_generation', label: 'Document generation', detail: 'Resume, invoice, quotation, and similar docs', icon: FileText },
  { key: 'cover_letter', label: 'Cover letter', detail: 'Per cover letter', icon: FileText },
  { key: 'purchase_order', label: 'Purchase order', detail: 'Per purchase order', icon: ShoppingCart },
  { key: 'business_plan_funding', label: 'Business Plan', detail: 'Per business plan', icon: Landmark },
  { key: 'business_grant_proposal', label: 'Business Grant Proposal', detail: 'Per proposal', icon: Award },
  { key: 'ngo_grant_proposal', label: 'NGO Grant Proposal', detail: 'Per proposal', icon: Handshake },
  { key: 'service_proposal', label: 'Service Proposal', detail: 'Per proposal', icon: Briefcase },
  { key: 'pdf_compression', label: 'PDF compression', detail: 'Per file', icon: FileArchive },
  { key: 'visa_photo', label: 'Visa / passport photo', detail: 'Per photo', icon: Camera },
];

/** Fallback if /service-credits fails — mirrors backend/config.py SERVICE_CREDITS. */
const SERVICE_CREDITS_FALLBACK = {
  document_generation: 3,
  cover_letter: 2,
  pdf_compression: 2,
  visa_photo: 4,
  business_proposal: 20,
  business_grant_proposal: 20,
  ngo_grant_proposal: 20,
  purchase_order: 5,
  service_proposal: 20,
};

/** Compact pack-card highlights (full list lives in Credit Usage Guide). */
const PACK_CARD_COST_KEYS = [
  'document_generation',
  'business_plan_funding',
  'service_proposal',
  'pdf_compression',
  'visa_photo',
];

export default function CreditsPage() {
  const { user, api, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [balance, setBalance] = useState(0);
  const [firstPurchaseEligible, setFirstPurchaseEligible] = useState(false);
  const [firstPurchaseBonus, setFirstPurchaseBonus] = useState(0);
  const [serviceCredits, setServiceCredits] = useState(SERVICE_CREDITS_FALLBACK);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pricingRes, balanceRes, creditsRes] = await Promise.all([
        api.get('/payments/pricing'),
        api.get('/credits/balance'),
        api.get('/service-credits').catch(() => ({ data: { services: SERVICE_CREDITS_FALLBACK } })),
      ]);
      const rawPacks = pricingRes.data.packs || [];
      // Trial Pack always leads — client-side sort so order is correct even if
      // an older API build returns Mongo insertion order (Trial last).
      const PACK_ORDER = { trial: 0, micro: 1, starter: 2, basic: 3, pro: 4, enterprise: 5 };
      const sorted = [...rawPacks].sort((a, b) => {
        const slugA = (a.id || '').split('_').pop().toLowerCase();
        const slugB = (b.id || '').split('_').pop().toLowerCase();
        return (PACK_ORDER[slugA] ?? 99) - (PACK_ORDER[slugB] ?? 99)
          || (a.credits || 0) - (b.credits || 0);
      });
      setPacks(sorted);
      setFirstPurchaseEligible(!!pricingRes.data.first_purchase_eligible);
      setFirstPurchaseBonus(pricingRes.data.first_purchase_bonus || 0);
      setBalance(balanceRes.data.balance);
      const services = creditsRes.data?.services;
      if (services && typeof services === 'object') {
        setServiceCredits({ ...SERVICE_CREDITS_FALLBACK, ...services });
      }
    } catch (error) {
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const costFor = (key) => resolveCreditCost(serviceCredits, key);

  const handlePurchase = async (packId) => {
    setPurchasing(packId);
    try {
      const callbackUrl = `${window.location.origin}/payment/callback`;
      const response = await api.post('/payments/flutterwave/initialize', {
        pack_id: packId,
        callback_url: callbackUrl,
      });
      if (response.data.mock) {
        const verifyRes = await api.get(`/payments/flutterwave/verify/${response.data.reference}`);
        if (verifyRes.data.status === 'success') {
          const bonus = verifyRes.data.bonus_credits || 0;
          if (bonus > 0) {
            toast.success(`Credits added — plus ${bonus} bonus credits for your first purchase!`);
          } else {
            toast.success('Credits Added Successfully');
          }
          await refreshUser();
          await fetchData();
        }
      } else if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('Could not start checkout. Please retry.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Payment initialization failed');
    } finally {
      setPurchasing(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatPrice = (pack) => {
    if (user?.pricing_region === 'PAYSTACK') {
      return `₦${pack.price_ngn.toLocaleString()}`;
    }
    return `$${pack.price_usd}`;
  };

  const getValuePerCredit = (pack) => {
    if (user?.pricing_region === 'PAYSTACK') {
      return (pack.price_ngn / pack.credits).toFixed(0);
    }
    return (pack.price_usd / pack.credits).toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/20">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 border-r border-border bg-white p-6 hidden lg:block">
        <div className="flex items-center gap-3 mb-10">
          <DocManiaLogoImage size={40} />
          <DocManiaLogo />
        </div>
        
        <nav className="space-y-2">
          <Link to="/dashboard">
            <Button variant="ghost" className="w-full justify-start h-11">
              <Settings className="mr-3 h-4 w-4" />
              Dashboard
            </Button>
          </Link>
          <Link to="/credits">
            <Button variant="secondary" className="w-full justify-start h-11">
              <CreditCard className="mr-3 h-4 w-4" />
              Get Credits
            </Button>
          </Link>
          <Link to="/jobs">
            <Button variant="ghost" className="w-full justify-start h-11">
              <History className="mr-3 h-4 w-4" />
              Job History
            </Button>
          </Link>
          <div className="pt-3 px-1">
            <CreditBadge balance={user?.credit_balance} />
          </div>
        </nav>
        
        <div className="absolute bottom-6 left-6 right-6 space-y-3">
          <div className="p-4 rounded-xl bg-muted/50">
            <div className="flex items-center gap-2 mb-1">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium truncate text-foreground text-sm">{user?.full_name}</span>
            </div>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-muted-foreground h-10" onClick={handleLogout}>
            <LogOut className="mr-3 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Nav */}
      <nav className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-border safe-area-top">
        <div className="flex items-center justify-between h-14 px-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium text-foreground">Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <CreditBadge balance={user?.credit_balance} />
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 p-6 lg:p-10">
        <div className="max-w-5xl mx-auto space-y-10">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Choose a Credit Plan</h1>
              <p className="text-muted-foreground">Select a plan that fits your workload. Larger plans offer better value per credit.</p>
            </div>
            <Card className="card-elevated bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 p-5">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Coins className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Current Balance</p>
                  <p className="text-3xl font-bold text-foreground">{balance}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Pricing Grid */}
          {firstPurchaseEligible && firstPurchaseBonus > 0 && (
            <div
              data-testid="first-purchase-bonus-banner"
              className="rounded-2xl border border-orange-200 bg-orange-50 p-4 sm:p-5 flex items-start gap-3"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-orange-100">
                <Sparkles className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground">First-time bonus — get +{firstPurchaseBonus} bonus credits</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Buy any standard pack on your first purchase (Trial Pack excluded) and we'll add {firstPurchaseBonus} bonus credits automatically.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            {packs.map((pack) => {
              const isTrial = !!(pack.is_trial || /_trial$/i.test(pack.id || '') || /^trial$/i.test(pack.name || ''));
              // Enterprise only — never Trial (cheapest $/credit is intentional intro pricing).
              const isBestValue = !isTrial && (
                /enterprise/i.test(pack.id || '') ||
                /enterprise/i.test(pack.name || '')
              );
              const isBasic = /basic/i.test(pack.id || '') || /basic/i.test(pack.name || '');
              // Trial never stacks with +3 — hide bonus copy even if an older API still sends it.
              const showBonus = !isTrial && (pack.bonus_credits || 0) > 0;
              return (
              <Card
                key={pack.id}
                className={`card-elevated relative overflow-hidden hover:shadow-xl transition-all duration-300 ${isBestValue ? 'border-primary shadow-lg ring-1 ring-primary/20' : ''} ${isTrial ? 'border-orange-300 ring-1 ring-orange-200' : ''}`}
                data-testid={`pricing-card-${pack.id}`}
              >
                {isBestValue && (
                  <div className="absolute top-0 left-0 right-0 bg-primary text-primary-foreground text-xs py-1.5 text-center font-medium">
                    Best Value
                  </div>
                )}
                {isTrial && (
                  <div className="absolute top-0 left-0 right-0 bg-orange-500 text-white text-xs py-1.5 text-center font-medium">
                    First purchase only
                  </div>
                )}
                <CardHeader className={isBestValue || isTrial ? 'pt-10' : ''}>
                  <CardTitle className="text-foreground text-lg">{pack.name}</CardTitle>
                  <CardDescription className="text-base">
                    {showBonus ? (
                      <>
                        <span className="font-semibold text-foreground">{pack.credits} credits</span>
                        <span className="ml-1 text-orange-600 font-semibold" data-testid={`pack-bonus-${pack.id}`}>
                          ({pack.effective_credits} with bonus)
                        </span>
                      </>
                    ) : (
                      <>{pack.credits} credits</>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <div className="text-3xl font-bold text-foreground">
                      {formatPrice(pack)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {user?.pricing_region === 'PAYSTACK' ? `₦${getValuePerCredit(pack)}` : `$${getValuePerCredit(pack)}`} per credit
                    </p>
                  </div>
                  
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    {PACK_CARD_COST_KEYS.map((key) => {
                      const item = CREDIT_USAGE_ITEMS.find((row) => row.key === key);
                      if (!item) return null;
                      return (
                        <li key={key} className="flex items-center gap-3">
                          <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Check className="h-3 w-3 text-primary" />
                          </div>
                          <span>{item.label} — {costFor(key)} credits</span>
                        </li>
                      );
                    })}
                  </ul>
                  
                  <Button 
                    size="sm"
                    className={`w-full min-w-0 font-medium ${isBasic ? 'glow-button' : ''}`}
                    variant={isBasic || isTrial ? "default" : "outline"}
                    disabled={purchasing === pack.id}
                    onClick={() => handlePurchase(pack.id)}
                    data-testid={`buy-btn-${pack.id}`}
                  >
                    {purchasing === pack.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Purchase Credits'
                    )}
                  </Button>
                </CardContent>
              </Card>
              );
            })}
          </div>

          {/* Service Costs Info */}
          <Card className="card-elevated" data-testid="service-costs-card">
            <CardHeader>
              <CardTitle className="text-foreground">Credit Usage Guide</CardTitle>
              <CardDescription>How credits are used for each service</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {CREDIT_USAGE_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const cost = costFor(item.key);
                  return (
                    <div
                      key={item.key}
                      className="flex items-center justify-between p-5 rounded-xl bg-muted/50"
                      data-testid={`credit-usage-${item.key}`}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-foreground block truncate">{item.label}</span>
                          <span className="text-sm text-muted-foreground">{item.detail}</span>
                        </div>
                      </div>
                      <Badge className="bg-primary/10 text-primary border-primary/20 border rounded-full px-3 py-1 font-semibold shrink-0 ml-3">
                        {cost}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

